// src/context/CurrencyContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/api";

const CurrencyContext = createContext(null);

const RATE_CACHE_KEY = "exchange_rate_cache";
const RATE_CACHE_TTL = 60 * 60 * 1000; // 1 hour
const FALLBACK_RATE  = 1500;

// ── Per-user storage key ──────────────────────────────────────────────────────
// Decodes the JWT payload to key the saved preference by user id, so switching
// accounts on the same browser never leaks one user's currency choice to another.
// Tries common field names in order — confirm the real one and reorder if needed.
function getUserStorageKey() {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return "currency_pref_guest";
    const payload = JSON.parse(atob(token.split(".")[1]));
    const uid = payload.userId || payload.id || payload._id || payload.sub;
    return uid ? `currency_pref_${uid}` : "currency_pref_guest";
  } catch {
    return "currency_pref_guest";
  }
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState]   = useState("NGN");
  const [rate, setRate]                 = useState(FALLBACK_RATE);
  const [isManualRate, setIsManualRate] = useState(false);
  const [rateLoading, setRateLoading]   = useState(true);

  // Load this user's saved preference
  useEffect(() => {
    const saved = localStorage.getItem(getUserStorageKey());
    setCurrencyState(saved === "USD" ? "USD" : "NGN");
  }, []);

  const setCurrency = useCallback((next) => {
    localStorage.setItem(getUserStorageKey(), next);
    setCurrencyState(next);
  }, []);

  const toggleCurrency = useCallback(() => {
    setCurrency(currency === "NGN" ? "USD" : "NGN");
  }, [currency, setCurrency]);

  // Fetch the exchange rate once, cached 1hr
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const cached = sessionStorage.getItem(RATE_CACHE_KEY);
        if (cached) {
          const { value, manual, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < RATE_CACHE_TTL) {
            setRate(value);
            setIsManualRate(manual);
            setRateLoading(false);
            return;
          }
        }
        const res = await api.get("/public/exchange-rate");
        if (res.data?.success && res.data.rate > 0) {
          setRate(res.data.rate);
          setIsManualRate(!!res.data.isManual);
          sessionStorage.setItem(
            RATE_CACHE_KEY,
            JSON.stringify({ value: res.data.rate, manual: !!res.data.isManual, timestamp: Date.now() })
          );
        }
      } catch {
        setRate(FALLBACK_RATE);
      } finally {
        setRateLoading(false);
      }
    };
    fetchRate();
  }, []);

  const format = useCallback(
    (amountNgn) => {
      if (currency === "USD") {
        const usd = Number(amountNgn) / rate;
        return `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      return `₦${Number(amountNgn).toLocaleString("en-NG")}`;
    },
    [currency, rate]
  );

  const convert = useCallback(
    (amountNgn) => (currency === "USD" ? Number(amountNgn) / rate : Number(amountNgn)),
    [currency, rate]
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, toggleCurrency, rate, isManualRate, rateLoading, format, convert }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside a CurrencyProvider");
  return ctx;
}